import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { workoutApi } from '../../api/workoutApi'
import type { WorkoutExerciseHistoryEntry, WorkoutSession } from '../../api/workoutApi'
import type { ApiError } from '../../api/client'
import { useTheme } from '../../hooks/useTheme'
import { useTranslation } from '../../i18n'
import { Button } from '../ui'
import { clearWorkoutSessionCache } from '../../utils/workoutSessionCache'
import { notifyWorkoutFinished } from '../../utils/workoutEvents'
import { captureEvent } from '../../posthog'
import { WorkoutSessionActiveView } from './WorkoutSessionActiveView'
import {
  isLogActualDirty,
  logActualFromExercise,
  type LogActual,
  type LogActuals,
} from './workoutLogActuals'
import {
  groupIndexForLogId,
  groupWorkoutExercises,
  type WorkoutGroup,
} from './workoutGroupHelpers'
import {
  dirtyLogIdsFromActuals,
  mergeActualsPreservingDirty,
  mergeServerSession,
  refreshAndSetCurrent,
  saveDirtyLogs,
  traineeWorkoutMutations,
} from './workoutSessionSync'

function actualsForGroup(group: WorkoutGroup | undefined): LogActuals {
  const next: LogActuals = {}
  for (const ex of group?.exercises ?? []) {
    next[ex.id] = logActualFromExercise(ex)
  }
  return next
}

function groupIsDirty(group: WorkoutGroup | undefined, actuals: LogActuals): boolean {
  if (!group) return false
  return group.exercises.some((ex) => isLogActualDirty(ex, actuals[ex.id]))
}

export default function WorkoutSessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()

  const [session, setSession] = useState<WorkoutSession | null>(null)
  const [groupIndex, setGroupIndex] = useState(0)
  const [actualsByLogId, setActualsByLogId] = useState<LogActuals>({})
  const [historyByName, setHistoryByName] = useState<Record<string, WorkoutExerciseHistoryEntry[]>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [finishing, setFinishing] = useState(false)
  const [canceling, setCanceling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const sessionCompletedRef = useRef(false)
  const actualsRef = useRef<LogActuals>({})
  const groupIndexRef = useRef(0)
  const sessionRef = useRef<WorkoutSession | null>(null)

  const groups = useMemo(
    () => groupWorkoutExercises(session?.exercises ?? []),
    [session?.exercises],
  )
  const currentGroup = groups[groupIndex]

  actualsRef.current = actualsByLogId
  groupIndexRef.current = groupIndex
  sessionRef.current = session

  const applySessionJump = useCallback((data: WorkoutSession) => {
    const gs = groupWorkoutExercises(data.exercises)
    const nextGroupIndex = groupIndexForLogId(gs, data.currentLogId)
    sessionRef.current = data
    setSession(data)
    setGroupIndex(nextGroupIndex)
    setActualsByLogId(actualsForGroup(gs[nextGroupIndex]))
  }, [])

  const applyMergedKeepGroup = useCallback((data: WorkoutSession) => {
    const current = sessionRef.current
    const dirty = dirtyLogIdsFromActuals(current?.exercises ?? [], actualsRef.current)
    const merged = mergeServerSession(current, data, dirty)
    const gs = groupWorkoutExercises(merged.exercises)
    const idx = Math.min(groupIndexRef.current, Math.max(0, gs.length - 1))
    sessionRef.current = merged
    setSession(merged)
    setActualsByLogId(mergeActualsPreservingDirty(
      merged,
      actualsRef.current,
      dirty,
      gs[idx]?.exercises.map(ex => ex.id),
    ))
  }, [])

  const loadSession = useCallback(async (opts?: { quiet?: boolean }) => {
    if (!sessionId) return
    if (!opts?.quiet) setLoading(true)
    if (!opts?.quiet) setError(null)
    try {
      const data = await workoutApi.getSession(Number(sessionId))
      if (opts?.quiet && sessionRef.current) {
        applyMergedKeepGroup(data)
      } else {
        applySessionJump(data)
      }
    } catch (err) {
      const apiError = err as ApiError
      if (apiError.status === 404) {
        notifyWorkoutFinished()
      }
      setError(apiError.message || t.workout.error.loadSessionFailed)
    } finally {
      if (!opts?.quiet) setLoading(false)
    }
  }, [sessionId, t.workout.error.loadSessionFailed, applySessionJump, applyMergedKeepGroup])

  const flushPendingSave = useCallback(() => {
    if (sessionCompletedRef.current) return
    const s = sessionRef.current
    const group = groupWorkoutExercises(s?.exercises ?? [])[groupIndexRef.current]
    const actuals = actualsRef.current
    if (!s || !group || s.finishedAt || !groupIsDirty(group, actuals)) return
    void saveDirtyLogs(traineeWorkoutMutations, s, group.exercises, actuals).catch(() => undefined)
  }, [])

  useEffect(() => {
    loadSession()
  }, [loadSession])

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flushPendingSave()
        return
      }
      if (document.visibilityState === 'visible' && !sessionCompletedRef.current) {
        void loadSession({ quiet: true })
      }
    }
    const onPageHide = () => flushPendingSave()
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onVisibility)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onVisibility)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [loadSession, flushPendingSave])

  const historyExerciseNamesKey = currentGroup
    ? [...new Set(currentGroup.exercises.map(ex => ex.exerciseName).filter(Boolean))].join('|')
    : ''

  useEffect(() => {
    const names = historyExerciseNamesKey ? historyExerciseNamesKey.split('|') : []
    if (names.length === 0) return
    let cancelled = false
    Promise.all(
      names.map(name =>
        workoutApi.getExerciseHistory(name)
          .then(res => [name, res.entries] as const)
          .catch(() => [name, [] as WorkoutExerciseHistoryEntry[]] as const),
      ),
    ).then((rows) => {
      if (cancelled) return
      const next: Record<string, WorkoutExerciseHistoryEntry[]> = {}
      for (const [name, entries] of rows) next[name] = entries
      setHistoryByName(next)
    })
    return () => { cancelled = true }
  }, [historyExerciseNamesKey])

  async function saveCurrentGroup(actuals = actualsByLogId): Promise<boolean> {
    const current = sessionRef.current
    const group = groupWorkoutExercises(current?.exercises ?? [])[groupIndexRef.current]
    if (!current || !group || current.finishedAt) return true
    if (!groupIsDirty(group, actuals)) return true
    setSaving(true)
    setError(null)
    try {
      const result = await saveDirtyLogs(
        traineeWorkoutMutations,
        current,
        group.exercises,
        actuals,
      )
      const gs = groupWorkoutExercises(result.session.exercises)
      sessionRef.current = result.session
      setSession(result.session)
      if (!result.ok) {
        setError(t.workout.conflict.log)
        setActualsByLogId(mergeActualsPreservingDirty(
          result.session,
          actuals,
          result.remainingDirty,
          gs[groupIndexRef.current]?.exercises.map(ex => ex.id),
        ))
        return false
      }
      setActualsByLogId(actualsForGroup(gs[groupIndexRef.current]))
      return true
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.workout.error.saveFailed)
      return false
    } finally {
      setSaving(false)
    }
  }

  function handleActualChange(logId: number, next: LogActual) {
    setActualsByLogId(prev => {
      const merged = { ...prev, [logId]: next }
      actualsRef.current = merged
      return merged
    })
  }

  useEffect(() => () => flushPendingSave(), [flushPendingSave])

  async function goToGroupIndex(newIndex: number) {
    if (!session || newIndex < 0 || newIndex >= groups.length || newIndex === groupIndex) return
    await saveCurrentGroup()
    const latest = sessionRef.current ?? session
    const targetGroup = groupWorkoutExercises(latest.exercises)[newIndex]
    const targetLog = targetGroup?.exercises[0]
    if (!targetLog) return
    const dirty = dirtyLogIdsFromActuals(latest.exercises, actualsRef.current)
    try {
      const result = await refreshAndSetCurrent(
        traineeWorkoutMutations,
        latest,
        targetLog.id,
        dirty,
      )
      if (result.staleSession) {
        applyMergedKeepGroup(result.session)
        setError(t.workout.conflict.session)
        return
      }
      setSession(result.session)
      setGroupIndex(newIndex)
      setActualsByLogId(actualsForGroup(groupWorkoutExercises(result.session.exercises)[newIndex]))
      setError(null)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.workout.error.saveFailed)
      setGroupIndex(newIndex)
      setActualsByLogId(actualsForGroup(targetGroup))
    }
  }

  async function finish() {
    if (!session) return
    setFinishing(true)
    setError(null)
    try {
      await saveCurrentGroup().catch(() => undefined)
      await workoutApi.finishSession(session.id)
      captureEvent('workout_finished', { session_id: session.id })
      sessionCompletedRef.current = true
      clearWorkoutSessionCache(session.id)
      notifyWorkoutFinished()
      navigate('/training/start', { replace: true })
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.workout.error.finishFailed)
    } finally {
      setFinishing(false)
    }
  }

  async function cancel() {
    if (!session) return
    if (!confirm(t.workout.cancelConfirm)) return
    setCanceling(true)
    setError(null)
    try {
      await workoutApi.cancelSession(session.id)
      captureEvent('workout_cancelled', { session_id: session.id })
      sessionCompletedRef.current = true
      clearWorkoutSessionCache(session.id)
      notifyWorkoutFinished()
      navigate('/training/start', { replace: true })
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.workout.error.cancelFailed)
    } finally {
      setCanceling(false)
    }
  }

  if (loading) {
    return (
      <div className="px-4 py-8 text-sm" style={{ color: theme.text.muted }}>
        {t.workout.loading}
      </div>
    )
  }

  if (!session || !currentGroup) {
    return (
      <div className="px-4 py-8">
        <p className="text-sm mb-4" style={{ color: theme.text.muted }}>
          {error || t.workout.error.loadSessionFailed}
        </p>
        <Button variant="secondary" onClick={() => navigate('/training/start')}>
          {t.workout.backToStart}
        </Button>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col px-4 pt-4 pb-4">
      <WorkoutSessionActiveView
        blockName={session.blockName}
        groupIndex={groupIndex}
        groups={groups}
        actualsByLogId={actualsByLogId}
        history={historyByName[currentGroup.exercises[0]?.exerciseName] ?? []}
        historyByExerciseName={historyByName}
        historySessionId={session.id}
        saving={saving}
        finishing={finishing}
        canceling={canceling}
        finished={Boolean(session.finishedAt)}
        error={error}
        onActualChange={handleActualChange}
        onPrev={() => void goToGroupIndex(groupIndex - 1)}
        onNext={() => void goToGroupIndex(groupIndex + 1)}
        onFinish={() => void finish()}
        onCancel={() => void cancel()}
      />
    </div>
  )
}
